import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAssetsTypeList } from '../../actions/inventory';
import { matchAssetTypeOption, normalizeOcrValue } from './pavAssetUtils';

const MAX_ATTEMPTS_PER_TERM = 2;

// One asset-type catalogue shared by every card on a PAV screen.
//
// Two things about the underlying store make per-card lookups unsafe, so they
// are centralised here:
//   * GET_ASSETS_TYPE_LIST_REQUEST is a takeLatest saga, so simultaneous
//     searches cancel each other and only the last one's results ever land.
//     Requests are therefore queued and dispatched strictly one at a time.
//   * state.inventory.assetsTypeDropdown is app-global and is replaced (not
//     merged) by an empty-query fetch, and cleared when any other <AssetType>
//     unmounts. A local union of every option ever seen keeps matching stable
//     regardless of what the shared slice currently holds.
export default function usePavAssetTypes({ orgId }) {
    const dispatch = useDispatch();
    const assetsTypeDropdown = useSelector((state) => state.inventory.assetsTypeDropdown);
    const isLoading = useSelector((state) => state.loading.isAssetsTypeDropdownLoading);
    const [catalogue, setCatalogue] = useState([]);
    // Per-term lifecycle: 'queued' -> 'pending' -> 'settled'. A term is only
    // settled once its response has come back, so a request cancelled by
    // takeLatest is retried instead of being silently marked as done.
    const [termVersion, setTermVersion] = useState(0);
    const termsRef = useRef(new Map());
    const queueRef = useRef([]);
    const inFlightRef = useRef(null);
    const hasRequestedFullListRef = useRef(false);

    const dispatchSearch = useCallback((term) => {
        inFlightRef.current = term;
        const entry = termsRef.current.get(term);
        termsRef.current.set(term, { ...entry, state: 'pending', attempts: (entry?.attempts || 0) + 1 });
        dispatch(getAssetsTypeList({ limit: 100, page: 1, query: entry.searchValue, dropdown: true, payload: { org_id: orgId } }));
        setTermVersion((current) => current + 1);
    }, [dispatch, orgId]);

    const pump = useCallback(() => {
        if (inFlightRef.current || isLoading || queueRef.current.length === 0) return;
        dispatchSearch(queueRef.current.shift());
    }, [dispatchSearch, isLoading]);

    // Load the whole catalogue up front so there is always a baseline to match
    // against and a populated list for manual selection.
    useEffect(() => {
        if (!orgId || hasRequestedFullListRef.current) return;
        hasRequestedFullListRef.current = true;
        dispatch(getAssetsTypeList({ limit: 100, page: 1, query: '', dropdown: true, payload: { org_id: orgId } }));
    }, [dispatch, orgId]);

    useEffect(() => {
        const incoming = Array.isArray(assetsTypeDropdown) ? assetsTypeDropdown : [];
        if (incoming.length === 0) return;
        setCatalogue((current) => {
            const byId = new Map(current.map((option) => [String(option.id ?? option.value ?? option.key), option]));
            let added = 0;
            incoming.forEach((option) => {
                const id = String(option?.id ?? option?.value ?? option?.key ?? '');
                if (!id || byId.has(id)) return;
                byId.set(id, option);
                added += 1;
            });
            return added > 0 ? Array.from(byId.values()) : current;
        });
    }, [assetsTypeDropdown]);

    // A finished request frees the queue. The in-flight term settles once the
    // catalogue can answer it or its retries run out, so a request cancelled by
    // takeLatest gets another turn instead of being written off as answered.
    useEffect(() => {
        if (isLoading) return;
        const term = inFlightRef.current;
        if (term) {
            inFlightRef.current = null;
            const entry = termsRef.current.get(term);
            if (entry) {
                const isAnswered = Boolean(matchAssetTypeOption(catalogue, entry.searchValue));
                if (isAnswered || entry.attempts >= MAX_ATTEMPTS_PER_TERM) {
                    termsRef.current.set(term, { ...entry, state: 'settled' });
                } else {
                    queueRef.current.push(term);
                }
            }
            setTermVersion((current) => current + 1);
        }
        pump();
    }, [isLoading, catalogue, pump]);

    const requestAssetType = useCallback((searchValue) => {
        const term = normalizeOcrValue(searchValue);
        if (!term || !orgId || termsRef.current.has(term)) return;
        termsRef.current.set(term, { state: 'queued', attempts: 0, searchValue: String(searchValue) });
        queueRef.current.push(term);
        setTermVersion((current) => current + 1);
        pump();
    }, [orgId, pump]);

    // Deliberately independent of the global loading flag: deriving "still
    // resolving" from it would unmount a card's asset-type picker whenever any
    // other dropdown in the app happened to be loading, including the search
    // fired by the user's own typing.
    const isTermSettled = useCallback(
        (searchValue) => termsRef.current.get(normalizeOcrValue(searchValue))?.state === 'settled',
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [termVersion]
    );

    const resolveAssetType = useCallback((ocrValue) => matchAssetTypeOption(catalogue, ocrValue), [catalogue]);

    return useMemo(() => ({
        catalogue,
        resolveAssetType,
        requestAssetType,
        isTermSettled,
    }), [catalogue, resolveAssetType, requestAssetType, isTermSettled]);
}
